// components/BookPayDeleteCard.jsx
import React, {useContext, useState} from "react";
import { Card, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { payDeleteBook } from "../../http/bookAPI";
import {Context} from "../../index";
import {check} from "../../http/userAPI";

// ---- Pay Delete: prices by plan (USD)
export const PAY_DELETE_PRICES = Object.freeze({
    FREE: 14.99,
    ESSENTIAL: 12.99,
    PREMIUM: 12.99,
    MAXIMUM: 12.99,
});

// normalize plan and return price (fallback -> FREE)
export const getPayDeletePrice = (planRaw) => {
    const plan = String(planRaw || 'FREE').toUpperCase();
    return PAY_DELETE_PRICES[plan] ?? PAY_DELETE_PRICES.FREE;
};

// try to infer plan from stores/props
export const getUserPlanForPayDelete = (userStore, bookData) =>
    (userStore?.user?.tariff_plan ??
        userStore?.tariff_plan ??
        bookData?.user?.tariff_plan ??
        bookData?.tariff_plan ??
        'FREE');

// Если у тебя уже есть этот helper — просто удалите дубликат ниже.
const threeLineToast = (title, message, newBalance) => {
    toast.success(
        <div>
            <div style={{ fontSize: "14px", fontWeight: "bold", lineHeight: "1.4" }}>{title}</div>
            <div style={{ fontSize: "12px", lineHeight: "1.2", marginTop: "4px" }}>{message}</div>
            <div style={{ fontSize: "12px", lineHeight: "1.2", marginTop: "4px" }}>{newBalance}</div>
        </div>,
        { position: "top-center", autoClose: 8000 }
    );
};

const BookPayDelete = ({ bookId, bookData, onSuccess }) => {
    const [agree, setAgree] = useState(false);
    const [loading, setLoading] = useState(false);
    const { user } = useContext(Context);



    const planForPrice = getUserPlanForPayDelete(user, bookData);
    const payDeletePrice = getPayDeletePrice(planForPrice);

    const handlePayDelete = async () => {
        try {
            setLoading(true);
            const res = await payDeleteBook(bookId);
            // ожидается { title, message, newBalance }
            threeLineToast(res.title, res.message, res.newBalance);
            if (typeof onSuccess === "function") onSuccess(res);

        } catch (err) {
            const msg = err?.response?.data?.message || "Failed to delete the book.";
            toast.error(msg, { position: "bottom-center", autoClose: 3000, hideProgressBar: false });
        } finally {
            setLoading(false);
            const data = await check();
            user.setUser(data);
        }
    };

    return (
        <Card className="p-0 w-100 mt-2">
            <Card.Header className="py-1 d-flex justify-content-between align-items-center">
        <span className="text-muted fw-semibold">
          Remove This Book/Product from Your {planForPrice} Tariff Plan (Hide)
        </span>
                {/* можно добавить подсказку/бейдж здесь при желании */}
            </Card.Header>
                <Card.Body>
                    <div className="small text-muted mb-2">

                        <h6>This book/product has active or confirmed assignments and therefore cannot be fully deleted. </h6>

                            <p className="mb-1">
                                Because this book/product already has active or confirmed assignments, it cannot be fully deleted.
                                If you currently have only assignments that are still in the <span className="fw-semibold">searching for a participants</span> stage,
                                you can cancel them and the book/product will become available for a complete and irreversible deletion.
                                But if the book/product has assignments that are <span className="fw-semibold">in progress</span> or <span className="fw-semibold">completed</span>,
                                deletion is not possible, as it would damage data integrity. A book/product cannot simply disappear from your readers’/participants’ accounts.
                            </p>

                            <p className="mb-1">
                                We understand that sometimes you just need to remove a book/product — not everyone wants to upgrade their plan or use subscriptions.
                                That’s why we built an option that can help in such situations: simply <span className="fw-semibold">hide this book/product from your account</span>.
                                It will “pretend to be deleted”: it will disappear from your account and <span className="fw-semibold">won’t count toward your plan</span>,
                                yet it will still remain in your readers’ accounts. If this works for you — go ahead.
                            </p>

                            <p className="mb-1">
                                This paid action will <span className="fw-semibold">exclude this book/product from your plan limits</span> — it will no longer count toward your {planForPrice} plan.
                                The book/product record itself <span className="fw-semibold">will remain</span> in the system (history, stats, and past assignments are kept).
                                This is <span className="fw-semibold">not</span> a full deletion.
                            </p>

                        <p className="mb-1">
                            A One Time Fee of <span className="fw-bold">${payDeletePrice}</span> will be charged from your <span className="fw-semibold">USD Balance</span>. Current state: <strong>${Number(user?.user?.lr_balance ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        </p>

                        <p className="mb-2">
                            Please double-check that this is the correct book/product you want to remove from your account list:
                        </p>

                        <div className="border border-secondary rounded px-3 py-3 bg-warning bg-opacity-25">
                            <div className="fs-6"><span className="fw-semibold ">Title:</span> {bookData?.title || "—"}</div>
                            {bookData?.subtitle ? (
                                <div><span className="fw-semibold"> {bookData?.book_type === "product" ? " Manufacturer: " : " Subtitle: "}</span> {bookData?.subtitle}</div>
                            ) : null}
                            <div><span className="fw-semibold">{bookData?.book_type === "product" ? " Seller: " : " Author: "}</span> {bookData?.author || "—"}</div>
                            <div><span className="fw-semibold">{bookData?.book_type === "product" ? " Product ID: " : " Book ID: "}</span> {bookData?.id || "—"}</div>

                        </div>
                    </div>

                    <div className="small text-muted mb-2">
                        <p className="mb-1">
                            Please double-check the details of the book/product you’re about to hide from this account (Title, Subtitle/Manufacturer, Author/Seller),
                            and tick the checkbox below to confirm you fully understand this action.
                        </p>
                    </div>


                    <Card className="p-2 mt-2 small">
                    <Form.Check
                        className="custom-checkbox mb-0"
                        type="checkbox"
                        id="agree-pay-delete"
                        checked={agree}
                        onChange={(e) => setAgree(e.target.checked)}
                        label={
                            <span className="ms-1">
              <span className="fw-semibold">I Fully Understand</span> — the book/product will be excluded from plan limits, hidden in this account, and the one time fee will be charged to my USD balance.
   </span>
                        }
                    />
                </Card>



                <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button
                        variant="secondary"
                        size="sm"
                        className="px-3"
                        disabled={loading}
                        onClick={() => {
                            setAgree(false);
                            user?.setActiveComponent("BookFull");
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="danger"
                        size="sm"
                        className="px-3"
                        disabled={!agree || loading}
                        onClick={handlePayDelete}
                    >
                        {loading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Processing…
                            </>
                        ) : (
                            <>
      <span className="d-none d-sm-inline">
        Hide this Book/Product for a One Time Fee (${payDeletePrice})
      </span>
                                <span className="d-inline d-sm-none">
        Hide for a Fee (${payDeletePrice})
      </span>
                            </>
                        )}
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

export default BookPayDelete;
